// src/components/TermsOfUseModal/content/en.tsx
import { FC } from 'react';

const EnContent: FC = () => (
  <>
    <h2>GENERAL TERMS AND CONDITIONS OF USE AND SALE</h2>
    <h3>The Patch Platform</h3>

    <h3>Preamble</h3>
    <p>
      These General Terms and Conditions of Use and Sale (hereinafter the <strong>&quot;Terms&quot;</strong>) are intended to define the conditions under which the company <strong>The Patch</strong>{' '}
      makes available to users a digital platform designed to assist with job application and interview preparation, based on artificial intelligence technologies.
    </p>
    <p>
      Where access to the platform is provided through an educational institution, a company, or any other partner organization (hereinafter a <strong>&quot;Client Organization&quot;</strong>),
      specific contractual conditions may apply in addition to, or in substitution for, these Terms.
    </p>

    <hr />

    <h3>Article 1 – Scope of the Services</h3>
    <p>The Patch is an online platform enabling, in particular:</p>
    <ul>
      <li>automated analysis of résumés (CVs);</li>
      <li>analysis of cover letters;</li>
      <li>AI-assisted résumé generation;</li>
      <li>AI-assisted cover letter generation;</li>
      <li>personalized mock job interviews;</li>
      <li>delivery of detailed post-interview feedback;</li>
      <li>matching with job opportunities adapted to the user&apos;s profile;</li>
      <li>direct application to certain job offers via the platform.</li>
    </ul>
    <p>
      These services are provided for <strong>training and educational purposes only</strong> and do not constitute recruitment advice, career counseling, or a guarantee of employment.
    </p>

    <hr />

    <h3>Article 2 – Access to the Services</h3>
    <p>Access to The Patch is reserved for individuals of legal age who have the legal capacity to enter into contracts.</p>
    <p>The user undertakes to:</p>
    <ul>
      <li>create only one personal account;</li>
      <li>provide accurate, complete, and up-to-date information;</li>
      <li>maintain the confidentiality of their login credentials.</li>
    </ul>
    <p>Any fraudulent, abusive, or unauthorized use may result in the suspension or deletion of the account.</p>

    <hr />

    <h3>Article 3 – Right of Use</h3>
    <p>The Patch grants the user a personal, non-exclusive, non-transferable, and non-assignable right to use the platform strictly for personal and non-commercial purposes.</p>
    <p>Any professional, commercial, or resale use is strictly prohibited without prior written authorization from The Patch.</p>

    <hr />

    <h3>Article 4 – Personal Data and User Content</h3>
    <p>The user acknowledges and accepts that the use of The Patch involves the processing of personal data, including but not limited to:</p>
    <ul>
      <li>identification data;</li>
      <li>résumés and cover letters;</li>
      <li>written or oral responses provided during interview simulations;</li>
      <li>results, scores, and usage history.</li>
    </ul>
    <p>
      The terms governing the collection, processing, storage, and deletion of such data are detailed in the <strong>Privacy Policy</strong>.
    </p>
    <p>The user retains ownership of their content but grants The Patch a limited license strictly necessary for the operation of the services.</p>

    <hr />

    <h3>Article 5 – Pricing and Payment Terms</h3>
    <p>Access to The Patch may be:</p>
    <ul>
      <li>free of charge (with limited functionalities); or</li>
      <li>subject to payment (subscription-based).</li>
    </ul>
    <p>Prices are stated in euros, inclusive of all applicable taxes, and are payable at the time of subscription.</p>
    <p>Unless otherwise required by applicable law, no refunds shall be granted in the event of voluntary discontinuation of use by the user.</p>

    <hr />

    <h3>Article 6 – Intellectual Property</h3>
    <p>All elements of The Patch platform, including but not limited to:</p>
    <ul>
      <li>software architecture;</li>
      <li>algorithms;</li>
      <li>analysis models;</li>
      <li>interfaces;</li>
      <li>content generated by the platform,</li>
    </ul>
    <p>are protected under intellectual property laws and remain the exclusive property of The Patch.</p>
    <p>Any unauthorized reproduction, modification, distribution, or exploitation is strictly prohibited.</p>

    <hr />

    <h3>Article 7 – Availability and Operation</h3>
    <p>The Patch endeavors to ensure continuous access to the platform but does not guarantee uninterrupted availability.</p>
    <p>The user acknowledges that:</p>
    <ul>
      <li>the service may contain errors or experience interruptions;</li>
      <li>The Patch is subject to an obligation of means, not an obligation of results.</li>
    </ul>

    <hr />

    <h3>Article 8 – Limitation of Liability</h3>
    <p>The Patch does not guarantee the scheduling of an interview or the securing of employment.</p>
    <p>The user remains solely responsible for:</p>
    <ul>
      <li>their decisions;</li>
      <li>their use of generated content;</li>
      <li>applications submitted via the platform.</li>
    </ul>
    <p>
      The Patch&apos;s total liability shall be strictly limited to the total amount paid by the user during the twelve (12) months preceding the event giving rise to liability, capped at a maximum of{' '}
      <strong>€5,000</strong>.
    </p>

    <hr />

    <h3>Article 9 – Termination</h3>
    <p>The user may delete their account at any time through their personal account space.</p>
    <p>The Patch reserves the right to suspend or terminate an account in the event of:</p>
    <ul>
      <li>breach of these Terms;</li>
      <li>abusive or fraudulent use;</li>
      <li>harm to the integrity of the service.</li>
    </ul>
    <p>No compensation shall be payable in such circumstances.</p>

    <hr />

    <h3>Article 10 – Technical Compatibility</h3>
    <p>The Patch is optimized for recent versions of major browsers (Chrome, Safari, Firefox).</p>
    <p>Proper functioning on other environments or outdated versions is not guaranteed.</p>

    <hr />

    <h3>Article 11 – Confidentiality and Trade Secrets</h3>
    <p>The user undertakes not to:</p>
    <ul>
      <li>reproduce;</li>
      <li>disclose;</li>
      <li>commercially exploit</li>
    </ul>
    <p>any elements, methods, or content of the platform, which are protected as trade secrets.</p>

    <hr />

    <h3>Article 12 – Modification of Services and Terms</h3>
    <p>The Patch reserves the right to modify:</p>
    <ul>
      <li>its functionalities;</li>
      <li>access conditions;</li>
      <li>these Terms.</li>
    </ul>
    <p>Any material modification shall be communicated in advance.</p>
    <p>Continued use of the services constitutes acceptance of the updated Terms.</p>

    <hr />

    <h3>Article 13 – Governing Law and Jurisdiction</h3>
    <p>
      These Terms are governed by <strong>French law</strong>.
    </p>
    <p>
      Any dispute shall fall under the exclusive jurisdiction of the courts of the judicial district in which The Patch&apos;s registered office is located, unless mandatory legal provisions provide
      otherwise.
    </p>

    <hr />

    <h3>Article 14 – Entire Agreement</h3>
    <p>These Terms constitute the entire agreement between The Patch and the user.</p>
    <p>The invalidity of any provision shall not affect the validity of the remaining provisions.</p>
  </>
);

export default EnContent;
